import { supabase } from '../supabase';
import { isCompanyProfileFilled, type OrgOnboardingSnapshot } from './reconcileChecklist';

/**
 * Fetch dello stato reale dell'organizzazione per la riconciliazione della
 * checklist onboarding (vedi reconcileChecklist.ts per i criteri).
 * Separato dalla logica pura per tenerla testabile senza il client supabase.
 */

async function safeCount(query: PromiseLike<{ count: number | null; error: unknown }>): Promise<number> {
  try {
    const { count, error } = await query;
    if (error) return 0;
    return count ?? 0;
  } catch {
    // Tabella non leggibile per questo ruolo (RLS/grant): il task resta
    // event-driven, nessun errore mostrato all'utente.
    return 0;
  }
}

/** Legge lo stato reale dell'org. Ogni check è fail-safe (errore → 0/false). */
export async function fetchOrgOnboardingSnapshot(
  organizationId: string,
): Promise<OrgOnboardingSnapshot> {
  const countBy = (table: string) =>
    safeCount(
      supabase
        .from(table)
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId),
    );

  const settingsPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('vat_number, street_address')
        .eq('organization_id', organizationId)
        .order('updated_at', { ascending: false })
        .limit(1);
      if (error) return null;
      return data?.[0] ?? null;
    } catch {
      return null;
    }
  })();

  const [
    settingsRow,
    pipelineCount,
    stageCount,
    clientCount,
    dealCount,
    salesTargetCount,
    memberCount,
    workflowCount,
    integrationCount,
    wahaConfigCount,
  ] = await Promise.all([
    settingsPromise,
    countBy('lead_pipelines'),
    countBy('lead_pipeline_stages'),
    countBy('clients'),
    countBy('deals'),
    countBy('sales_targets'),
    countBy('user_profiles'),
    countBy('automation_workflows'),
    safeCount(
      supabase
        .from('api_configurations')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .neq('service_name', 'waha'),
    ),
    safeCount(
      supabase
        .from('api_configurations')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', organizationId)
        .eq('service_name', 'waha'),
    ),
  ]);

  return {
    companyProfileFilled: isCompanyProfileFilled(settingsRow),
    pipelineCount,
    stageCount,
    clientCount,
    dealCount,
    salesTargetCount,
    memberCount,
    workflowCount,
    integrationCount,
    wahaConfigCount,
  };
}
